import { useLazyQuery, useQuery } from '@apollo/client';
import { Box, Grid, Stack, TablePagination, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Count, CountSeparator } from '~/components/count';
import { Pagination } from '~/components/data-grid';
import { EmptyState } from '~/components/empty-state';
import { FolderBreadcrumbs } from '~/components/folder-breadcrumbs';
import { SearchBar } from '~/components/forms/search-bar';
import { useAppContext, useFolderContext } from '~/contexts';
import { useUploadDispatch } from '~/contexts/upload';
import type { ContentKind } from '~/generated/graphql';
import { useLastFolder } from '~/hooks/folders';
import { usePollInterval } from '~/hooks/polling';
import UploadMediaIllustration from '~/images/illustrations/upload-media.svg';
import { PER_PAGE } from '~/lib/pagination';
import { ContentSelectDocument, ContentSelectSearchDocument } from '../index.generated';
import { ActionsContainer, AppCard, FolderCard, MediaItemCard, UploadButton } from './';

export interface ContentGridProps {
  actions?: React.ReactNode;
  aspectRatio?: number;
  /** Apps to leave out, e.g. the one being edited. */
  excludeContentItemIds?: number[];
  /** Uploads only add media, so pickers of other content hide the button. */
  hideUpload?: boolean;
  kind?: ContentKind[];
  maxHeight?: string;
  onClick: (id: string) => void;
  selectedIds: string[];
  rememberLastFolder?: boolean;
  storageContext?: string;
}

export const ContentGrid = ({
  actions,
  aspectRatio,
  excludeContentItemIds,
  hideUpload = false,
  kind,
  maxHeight = 'calc(95vh - 316px)',
  onClick,
  selectedIds,
  rememberLastFolder = false,
  storageContext = 'media',
}: ContentGridProps) => {
  const { t } = useTranslation();
  const { currentNetwork } = useAppContext();
  const { folderTree, landingFolderId } = useFolderContext();

  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(PER_PAGE);

  const uploadDispatch = useUploadDispatch();
  const { pollInterval, setFastPolling } = usePollInterval(0, 1_000);

  const [contentFolderId, setContentFolderId] = useLastFolder({
    enabled: rememberLastFolder,
    defaultFolderId: landingFolderId,
    storageContext,
  });

  const changeFolder = (id: number) => {
    setPage(1);
    setContentFolderId(id);
  };

  const { data, startPolling, stopPolling } = useQuery(ContentSelectDocument, {
    variables: {
      condition: { aspectRatio, excludeContentItemIds, kind },
      contentFolderId,
      networkId: currentNetwork.id,
      page,
      perPage,
    },
  });

  const [searchContent, { data: searchData }] = useLazyQuery(ContentSelectSearchDocument, {
    variables: {
      condition: { aspectRatio, excludeContentItemIds, kind, search },
      networkId: currentNetwork.id,
      page,
      perPage,
    },
  });

  useEffect(() => {
    startPolling(pollInterval);
    return () => stopPolling();
  }, [pollInterval, startPolling, stopPolling]);

  useEffect(() => {
    const listener = () => setFastPolling(30);
    uploadDispatch({ type: 'addListener', listener });
    return () => uploadDispatch({ type: 'removeListener', listener });
  }, [setFastPolling, uploadDispatch]);

  const contentFolder = data?.network?.contentFolder;
  const folders = search ? [] : contentFolder?.children ?? [];
  const items = (search ? searchData?.network?.content.nodes : contentFolder?.content.nodes) ?? [];
  const totalCount =
    (search
      ? searchData?.network?.content.pageInfo.nodesCount
      : contentFolder?.content.pageInfo.nodesCount) ?? 0;

  return (
    <>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', sm: 'center' }}
        spacing={1}
        mb={1}
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          <SearchBar
            onChange={(term) => {
              setPage(1);
              setSearch(term);
              void searchContent();
            }}
            search={search}
            placeholder={t('common.actions.search')}
          />
          {actions}
        </Stack>
        {!hideUpload && (
          <UploadButton disabled={!contentFolder?.canUpdate.value} folderId={contentFolderId} />
        )}
      </Stack>

      <ActionsContainer>
        {!search && contentFolder ? (
          <FolderBreadcrumbs
            allItems={() => changeFolder(folderTree.id)}
            changeFolder={changeFolder}
            folder={contentFolder}
            label={t('pages.playlists.components.add_content.all_items')}
          />
        ) : (
          <Box />
        )}

        <Box sx={{ display: 'flex', alignItems: 'flex-end' }}>
          {!search && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                paddingRight: 1,
              }}
            >
              <Count
                selectedCount={0}
                totalCount={folders.length}
                thing="components.content_select.labels.folder_count_plural"
              />
              <CountSeparator />
            </Box>
          )}
          <Count
            selectedCount={selectedIds.length}
            totalCount={items.length}
            thing="components.content_select.labels.thing_item_plural"
          />
        </Box>
      </ActionsContainer>

      <Box sx={{ height: maxHeight, overflow: 'auto', mt: 2, mx: -1, px: 1 }}>
        {folders.length === 0 && items.length === 0 ? (
          <EmptyState
            illustration={UploadMediaIllustration}
            header={t('pages.content.empty_state.header')}
            description={t('pages.content.empty_state.description')}
          />
        ) : (
          <>
            {folders.length > 0 && (
              <Box
                sx={{
                  paddingBottom: 4,
                  marginBottom: 4,
                  borderBottom: items.length > 0 ? 1 : 0,
                  borderBottomColor: 'borders.main',
                }}
              >
                <Typography variant="h5" sx={{ marginBottom: 2 }}>
                  {t('pages.playlists.components.add_content.folders', { count: folders.length })}
                </Typography>
                <Grid container spacing={2}>
                  {folders.map((folder) => (
                    <FolderCard
                      key={folder.id}
                      folder={folder}
                      onClick={() => changeFolder(folder.id)}
                    />
                  ))}
                </Grid>
              </Box>
            )}
            {items.length > 0 && (
              <Box sx={{ paddingBottom: 4 }}>
                <Typography variant="h5" sx={{ marginBottom: 2 }}>
                  {t('pages.playlists.components.add_content.items', { count: items.length })}
                </Typography>
                <Grid container spacing={2}>
                  {items.map((item) => {
                    const id = `${item.__typename}-${item.id}`;
                    return item.__typename === 'ContentItem' ? (
                      <AppCard
                        key={id}
                        app={item}
                        onClick={() => onClick(id)}
                        selected={selectedIds.includes(id)}
                      />
                    ) : (
                      <MediaItemCard
                        key={id}
                        mediaItem={item}
                        onClick={() => onClick(id)}
                        selected={selectedIds.includes(id)}
                      />
                    );
                  })}
                </Grid>
              </Box>
            )}
          </>
        )}
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <TablePagination
          ActionsComponent={Pagination}
          component="div"
          count={totalCount}
          labelRowsPerPage={t('common.pagination.per_page')}
          labelDisplayedRows={({ from, to, count }) =>
            t('common.pagination.displayed_rows', { from, to, count })
          }
          onPageChange={(_, newPage) => setPage(newPage + 1)}
          onRowsPerPageChange={(event) => {
            setPerPage(parseInt(event.target.value));
            setPage(1);
          }}
          page={page - 1}
          rowsPerPage={perPage}
          rowsPerPageOptions={[25, 50, 100]}
        />
      </Box>
    </>
  );
};
